<template>
  <div>
    <b-card>
      <TitleTable titleTable="Stock by facility" iconTable="fas fa-warehouse" />
      <b-row class="wl-filters m-1 mb-3 align-items-start">
        <b-col cols="12" md="4">
          <b-form-group label="Facility" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.facilities_id"
              :options="facilityOptions"
              :normalizer="facilityNormalizer"
              :multiple="true"
              :clearable="true"
              placeholder="All facilities"
              :disabled="optionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Category" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.inv_category_id"
              :options="categoryOptions"
              :normalizer="categoryNormalizer"
              :clearable="true"
              placeholder="All categories"
              :disabled="optionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4" class="d-flex justify-content-end align-items-end pb-1">
          <b-button
            variant="outline-primary"
            class="wl-filter-btn mt-2 mt-md-0"
            :disabled="optionsLoading || loading"
            @click="load">
            <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': loading }"></i>
            Refresh
          </b-button>
        </b-col>
      </b-row>

      <b-table
        :items="items"
        :fields="fields"
        bordered
        responsive
        show-empty
        empty-text="No stock records found">
        <template #cell(category)="data">
          {{ categoryLabel(data.item) }}
        </template>
        <template #cell(stock)="data">
          {{ formatNumber(data.value) }}
        </template>
        <template #cell(reorder_point)="data">
          <span :class="{ 'text-muted font-italic': isNullish(data.value) }">
            {{ displayValue(data.value) }}
          </span>
        </template>
        <template #cell(stock_status)="data">
          <b-badge :variant="statusVariant(data.item.stock_status)">
            {{ statusLabel(data.item.stock_status) }}
          </b-badge>
        </template>
      </b-table>
    </b-card>
    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import { BCard, BRow, BCol, BTable, BButton, BBadge, BFormGroup } from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import TitleTable from '@/components/TitleTable.vue'
import ToastNotification from '@/components/ToastNotification'
import invStockApi from '@/core/services/api/weightloss-inventory/stock'
import invCategoriesApi from '@/core/services/api/weightloss-inventory/categories'
import { facilitiesFilterParams } from '@/core/services/api/weightloss-inventory/facility'
import axiosF from '@/core/services/api/admin/facilities'

const STATUS_LABELS = {
  ok: 'OK',
  low: 'Low',
  critical: 'Critical',
  empty: 'Empty',
}

export default {
  name: 'TableInvStock',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BBadge,
    BFormGroup,
    Treeselect,
    TitleTable,
    ToastNotification,
  },
  data() {
    return {
      loading: false,
      optionsLoading: false,
      items: [],
      facilityOptions: [],
      categoryOptions: [],
      facilityNormalizer(node) {
        return { id: node.id, label: node.name }
      },
      categoryNormalizer(node) {
        return { id: node.id, label: node.label || node.name }
      },
      filters: {
        facilities_id: [],
        inv_category_id: null,
      },
      fields: [
        { key: 'name', label: 'Item' },
        { key: 'sku', label: 'SKU' },
        { key: 'unit', label: 'Unit' },
        { key: 'category', label: 'Category' },
        { key: 'stock', label: 'Stock' },
        { key: 'reorder_point', label: 'Reorder min.' },
        { key: 'stock_status', label: 'Status' },
      ],
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      this.optionsLoading = true
      try {
        const [facilitiesRes, categories] = await Promise.all([
          axiosF.facilityList(1000),
          invCategoriesApi.list(),
        ])
        this.facilityOptions = (facilitiesRes.data || []).map(f => ({
          id: f.id,
          name: f.name,
        }))
        this.categoryOptions = categories.map(c => ({ id: c.id, label: c.name }))
      } catch (e) {
        this.$refs.toast.danger(e.message || 'Could not load filter options')
      } finally {
        this.optionsLoading = false
      }
      this.load()
    },
    buildListParams() {
      const params = {
        ...facilitiesFilterParams(this.filters.facilities_id),
      }
      if (this.filters.inv_category_id != null && this.filters.inv_category_id !== '') {
        params.inv_category_id = Number(this.filters.inv_category_id)
      }
      return params
    },
    statusVariant(status) {
      return {
        ok: 'success',
        low: 'warning',
        critical: 'danger',
        empty: 'secondary',
      }[status] || 'secondary'
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status || '—'
    },
    categoryLabel(item) {
      if (item && item.category && item.category.name) return item.category.name
      const id = item && (item.inv_category_id || (item.category && item.category.id))
      if (id != null) {
        const c = this.categoryOptions.find(x => x.id === id || x.id === Number(id))
        return c ? c.label : `#${id}`
      }
      return '—'
    },
    isNullish(value) {
      return value === null || value === undefined || value === ''
    },
    displayValue(value) {
      return this.isNullish(value) ? '—' : value
    },
    formatNumber(value) {
      if (this.isNullish(value)) return '—'
      const n = Number(value)
      return Number.isNaN(n) ? value : n
    },
    async load() {
      this.loading = true
      try {
        this.items = await invStockApi.list(this.buildListParams())
      } catch (e) {
        this.$refs.toast.danger(e.message)
        this.items = []
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.wl-filter-btn {
  min-width: 7rem;
}
</style>
